import { Monitor } from '@phosphor-icons/react';
import type { Atmosphere } from '@shh/shared';
import { Button, ChoiceGroup, Text } from '@shh/ui';
import { useState } from 'react';
import { updateHousehold } from '../../data/api';
import { useFamily } from '../../data/family';
import { getZoom, setZoom, ZOOM_LEVELS, type Zoom } from '../../lib/display';
import { canFullscreen, getFullscreen, setFullscreen } from '../../lib/fullscreen';
import { atmosphereOf, showRestScreen } from '../../app/Rest';
import { FestiveSetting } from './ReminderSettings';
import { settingsText } from '../../i18n/settings';

type Strength = 'off' | 'subtle' | 'strong';

/** Settings → Anzeige: size on this device; daylight colours and the resting wall for the family. */
export function DisplaySettings() {
  return (
    <div className="flex flex-col gap-section">
      <SizeSetting />
      {canFullscreen() && <FullscreenSetting />}
      <DaylightSetting />
      <AtmosphereSetting />
      <FestiveSetting />
    </div>
  );
}

const SaveError = () => (
  <p role="alert" className="font-semibold text-feedback-danger">
    {settingsText.saveError}
  </p>
);

/**
 * Family Atmosphere: when and how the wall display rests. A family setting (parents set it
 * from their phone for the tablet on the wall); every choice is saved at once.
 */
function AtmosphereSetting() {
  const f = useFamily();
  const a = atmosphereOf(f);
  const [error, setError] = useState(false);
  const save = (patch: Partial<Atmosphere>) => {
    setError(false);
    void updateHousehold(f.householdId, { atmosphere: { ...a, ...patch } }).catch(() =>
      setError(true),
    );
  };
  return (
    <div className="flex flex-col gap-gap-lg">
      <ChoiceGroup<'on' | 'off'>
        label={settingsText.rest}
        description={settingsText.restHint}
        value={a.enabled ? 'on' : 'off'}
        onChange={(v) => save({ enabled: v === 'on' })}
        choices={[
          { value: 'on', label: settingsText.on },
          { value: 'off', label: settingsText.off },
        ]}
      />
      {a.enabled && (
        <>
          <ChoiceGroup<`${number}`>
            label={settingsText.restAfter}
            value={`${a.idleMinutes}`}
            onChange={(v) => save({ idleMinutes: Number(v) })}
            choices={[1, 2, 3, 5, 10].map((m) => ({
              value: `${m}` as const,
              label: settingsText.minutes(String(m)),
            }))}
          />
          <ChoiceGroup<string>
            label={settingsText.nightFrom}
            description={settingsText.nightHint}
            value={a.sleepFrom ?? 'off'}
            onChange={(v) => save({ sleepFrom: v === 'off' ? null : v })}
            choices={[
              { value: 'off', label: settingsText.never },
              { value: '21:00', label: settingsText.clock('21') },
              { value: '22:00', label: settingsText.clock('22') },
              { value: '23:00', label: settingsText.clock('23') },
            ]}
          />
          {a.sleepFrom && (
            <ChoiceGroup<string>
              label={settingsText.until}
              value={a.sleepTo}
              onChange={(v) => save({ sleepTo: v })}
              choices={[
                { value: '05:00', label: settingsText.clock('5') },
                { value: '06:00', label: settingsText.clock('6') },
                { value: '07:00', label: settingsText.clock('7') },
              ]}
            />
          )}
        </>
      )}
      {error && <SaveError />}
      <Button variant="secondary" icon={Monitor} className="self-start" onPress={showRestScreen}>
        {settingsText.showRest}
      </Button>
    </div>
  );
}

/** Only this device – the wall tablet wants larger text than a phone held in the hand. */
function SizeSetting() {
  const [zoom, setLocal] = useState<Zoom>(getZoom);
  return (
    <ChoiceGroup<`${Zoom}`>
      label={settingsText.size}
      description={settingsText.sizeHint}
      value={`${zoom}`}
      onChange={(v) => {
        const z = Number(v) as Zoom;
        setLocal(z);
        setZoom(z);
      }}
      choices={ZOOM_LEVELS.map((z) => ({ value: `${z}` as const, label: `${z} %` }))}
    />
  );
}

/** Only this device, like the size – the wall tablet goes full screen, the phone stays as it is. */
function FullscreenSetting() {
  const [on, setOn] = useState(getFullscreen);
  return (
    <ChoiceGroup<'on' | 'off'>
      label={settingsText.fullscreen}
      description={settingsText.fullscreenHint}
      value={on ? 'on' : 'off'}
      onChange={(v) => {
        setOn(v === 'on');
        setFullscreen(v === 'on');
      }}
      choices={[
        { value: 'on', label: settingsText.on },
        { value: 'off', label: settingsText.off },
      ]}
    />
  );
}

/**
 * How strongly the colours follow the time of day. One choice for the whole family (phones,
 * laptops, wall) – applied at once, no save button needed.
 */
function DaylightSetting() {
  const f = useFamily();
  const value: Strength = f.household?.daylight ?? 'subtle';
  const [error, setError] = useState(false);
  return (
    <div className="flex flex-col gap-gap-md">
      <ChoiceGroup<Strength>
        label={settingsText.daylight}
        description={settingsText.daylightHint}
        value={value}
        onChange={(daylight) => {
          setError(false);
          // Firestore reverts the optimistic value if the write is refused – say so instead of
          // silently jumping back.
          void updateHousehold(f.householdId, { daylight }).catch(() => setError(true));
        }}
        choices={[
          { value: 'strong', label: settingsText.strong },
          { value: 'subtle', label: settingsText.subtle },
          { value: 'off', label: settingsText.off },
        ]}
      />
      {error && <SaveError />}
      <Text size="sm" tone="muted">
        {settingsText.daylightAll}
      </Text>
    </div>
  );
}
